// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { State } from "../../types/types";

interface FloorsState {
  state: State;
  stateMessage: string | null;
  errorMessage: string | null;
  floors: FloorCount[] | null;
  totalCount: number | null;
}

const initialState: FloorsState = {
  state: State.idle,
  stateMessage: null,
  errorMessage: null,
  floors: null,
  totalCount: null,
};

export interface FloorCount {
  floorNo: string;
  sessionCount: number;
}

export const fetchFloorCounts = createAsyncThunk(
  "floors/fetchFloorCounts",
  async (args: { network: string; floor: number | null }, thunkAPI) => {
    return new Promise<{ floors: FloorCount[] | null }>((resolve, reject) => {
      const queryParams = `network=${args.network}${
        args.floor !== null ? `&floor=${args.floor}` : ""
      }`;
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.getFloorCount}?${queryParams}`)
        .then((response) => {
          resolve({ floors: response.data });
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }
);

const floorsSlice = createSlice({
  name: "floors",
  initialState,
  reducers: {
    resetFloorState: (state) => {
      state.state = State.idle;
      state.stateMessage = null;
      state.errorMessage = null;
      state.floors = null;
      state.totalCount = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFloorCounts.pending, (state) => {
        state.state = State.loading;
        state.stateMessage = "Fetching floor counts...";
        state.errorMessage = null;
      })
      .addCase(fetchFloorCounts.fulfilled, (state, action) => {
        state.floors = action.payload.floors;
        state.state = State.success;
        state.stateMessage = "Floor counts fetched successfully!";
      })
      .addCase(fetchFloorCounts.rejected, (state, action) => {
        state.state = State.failed;
        state.stateMessage = "Failed to fetch floor counts!";
        state.errorMessage = action.error.message || "Unknown error occurred";
      });
  },
});
export const { resetFloorState } = floorsSlice.actions;
export default floorsSlice.reducer;
